import { useMutation } from 'glimmer-apollo'
import { parse } from 'graphql'

type AuthorizePaymentMethodInput = {
  input: {
    quoteId: string
    idempotencyKey: string
    paymentMethod: string
    token: string
  }
}

type AuthorizePaymentMethodTypename =
  | 'AuthorizePaymentMethodSuccess'
  | 'GenericError'
  | 'TransactionError'
  | 'NotFoundError'
  | 'RequiresActionCardIssuerDiscount'

type TransactionErrorCode =
  | 'AMEX_DISABLE'
  | 'AMOUNT_TOO_LARGE'
  | 'AMOUNT_TOO_SMALL'
  | 'CONTACT_CUSTOMER'
  | 'DECLINED'
  | 'GENERIC_DECLINE'
  | 'INSUFFICIENT_FUNDS'
  | 'INSUFFICIENT_RESERVE_BALANCE'
  | 'INTEGRATION_ONLY'
  | 'NOT_PERMITTED'
  | 'PAYMENT_INFO'
  | 'REPORTED_LOST_STOLEN'
  | 'TRY_AGAIN'
  | 'UNHANDLED'

type AuthorizePaymentMethodResponse = {
  authorizePaymentMethod: {
    __typename?: AuthorizePaymentMethodTypename
    authorizedAmount?: { amount: string; currency: string }
    message?: string
    errorCode?: TransactionErrorCode
    consumerMessage?: string
    action?: string
    cardIssuerDiscountAmount?: { formatted: string }
  }
}

export function authorizePaymentMethod(
  context: Object,
  variables: AuthorizePaymentMethodInput
): Promise<AuthorizePaymentMethodResponse | undefined | null> {
  return new Promise((resolve, reject) => {
    const authorizePaymentMethodMutation = useMutation<AuthorizePaymentMethodResponse, AuthorizePaymentMethodInput>(context, () => [
      mutation,
      {
        onError: (error) => {
          reject(new Error('Failed to authorize payment method.', { cause: error }))
        },
        onComplete: (data: AuthorizePaymentMethodResponse | undefined | null) => {
          resolve(data)
        }
      }
    ])

    authorizePaymentMethodMutation.mutate(variables)
  })
}

const mutation = parse(`
  mutation AuthorizePaymentMethod($input: AuthorizePaymentMethodInput!) {
    authorizePaymentMethod(input: $input) {
      ... on AuthorizePaymentMethodSuccess {
        authorizedAmount {
          amount
          currency
        }
      }

      ... on GenericError {
        message
      }

      ... on TransactionError {
        errorCode
        message
        consumerMessage
      }

      ... on NotFoundError {
        message
      }

      ... on RequiresActionCardIssuerDiscount {
        action
        cardIssuerDiscountAmount {
          formatted
        }
      }
    }
  }
`)

export default mutation
